import React from "react";
import { isApiFileUrl, resolveFileUrl } from "../../utils/fileUrl";

type AppVideoProps = Omit<
  React.VideoHTMLAttributes<HTMLVideoElement>,
  "src" | "poster" | "crossOrigin"
> & {
  src?: string | null;
  poster?: string | null;
};

/**
 * `<video>` wrapper. Resolves both `src` and `poster` against the runtime
 * backend host, and attaches `crossOrigin="use-credentials"` when the resolved
 * `src` is an API file URL so the cookie scheme authenticates the request.
 * Note: `crossOrigin` is a single attribute on the element — it applies to
 * src, poster, and any track resources together. We base it on `src` because
 * mixing an API-hosted video with a third-party poster (or vice versa) is not
 * a real use case in this codebase.
 */
const AppVideo: React.FC<AppVideoProps> = ({ src, poster, ...rest }) => {
  const resolvedSrc = resolveFileUrl(src);
  const resolvedPoster = resolveFileUrl(poster);
  const crossOrigin = isApiFileUrl(resolvedSrc) ? "use-credentials" : undefined;
  return (
    <video
      src={resolvedSrc}
      poster={resolvedPoster}
      crossOrigin={crossOrigin}
      {...rest}
    />
  );
};

export default AppVideo;
